import { loadFromStorage, saveToStorage } from "@/lib/storage";
import { USERS, INITIAL_DAYS_DAVID, INITIAL_DAYS_SARA, LOCAL_STORAGE_KEYS } from "@/config/constants";
import { formatDateKey, isValidDate, getDayNameFromDateKey, addWeeksToDateKey, subWeeksToDateKey, parseDateKey, isFutureDateKey, subDaysToDateKey } from '@/lib/dateUtils';
import { getInitialDayDataForUser } from './dogDayManagerActions';

const MAX_WEEKS_AHEAD_TO_PREPOPULATE = 24; 
const MAX_WEEKS_BEHIND_TO_CONSIDER = 13; 
const DAYS_PAST_UNCHANGEABLE_THRESHOLD = 30;

export const initializeDogData = (selectedUser, selectedDayKey, currentDate) => {
  // ✅ FIRESTORE ES LA FUENTE DE VERDAD
  // No cargamos daysList desde localStorage - Firestore poblará los datos vía suscripción
  console.log('[INIT] Iniciando con daysList vacío - esperando datos de Firestore');
  
  // No restaurar completedDogs/urgentDogs desde localStorage.
  // Firestore sincroniza en tiempo real vía subscribeStates.
  // Comenzar siempre con estado limpio previene herencia de estados azul/rojo de sesiones anteriores.

  return {
    initialDaysList: {}, // ✅ Vacío - Firestore es la fuente de verdad
    initialCompletedDogs: {}, // ✅ Vacío - sincronizado desde Firestore en tiempo real
    initialUrgentDogs: {}, // ✅ Vacío - sincronizado desde Firestore en tiempo real
  };
};

export const handleDateChange = (date, setCurrentDate, setSelectedDayKey, setDaysList, getInitialDayData, selectedUser) => {
    if (date && isValidDate(date)) {
      const newKey = formatDateKey(date);
      const todayKey = formatDateKey(new Date());
      const unchangeablePastThresholdDateKey = subDaysToDateKey(todayKey, DAYS_PAST_UNCHANGEABLE_THRESHOLD);
      setCurrentDate(date);
      setSelectedDayKey(newKey);
      const permanentlyDeletedDogsRaw = loadFromStorage(LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS, [], selectedUser);
      const permanentlyDeletedDogs = Array.isArray(permanentlyDeletedDogsRaw) ? permanentlyDeletedDogsRaw : [];

      // LOG [PERMA_DEL_DBG]: Lectura inicial del localStorage
      if (permanentlyDeletedDogs.length > 0) {
        console.error("[PERMA_DEL_DBG] INIT_LOAD", JSON.stringify({
          profile: selectedUser,
          permanentlyDeletedDogsCount: permanentlyDeletedDogs.length,
          dogNames: permanentlyDeletedDogs,
          timestamp: new Date().toISOString()
        }));
      }

      setDaysList(prevDaysList => {
        let updatedDaysList = {...prevDaysList};
        let totalTargetsTouched = 0;
        
        const ensureDayExists = (keyToEnsure, forceInitialPopulationForPast = false) => {
          const isPastUnchangeable = parseDateKey(keyToEnsure) < parseDateKey(unchangeablePastThresholdDateKey);
          const isPastDay = parseDateKey(keyToEnsure) < parseDateKey(todayKey);

          if (isPastUnchangeable && updatedDaysList[keyToEnsure] && !forceInitialPopulationForPast) {
             const beforeLen = updatedDaysList[keyToEnsure].length;
             updatedDaysList[keyToEnsure] = updatedDaysList[keyToEnsure].filter(dog => !permanentlyDeletedDogs.includes(dog.name));
             const afterLen = updatedDaysList[keyToEnsure].length;
             updatedDaysList[keyToEnsure].sort((a,b) => (a.originalOrder || 0) - (b.originalOrder || 0));
             
             // LOG [PERMA_DEL_DBG]: Filtrado en días pasados no modificables
             const removedDogs = beforeLen - afterLen;
             if (removedDogs > 0) {
               totalTargetsTouched++;
               console.error("[PERMA_DEL_DBG] FILTER_UNCHANGEABLE_PAST", JSON.stringify({
                 profile: selectedUser,
                 dayKey: keyToEnsure,
                 beforeLen,
                 afterLen,
                 removedCount: removedDogs,
                 removedDogs: updatedDaysList[keyToEnsure].filter(d => permanentlyDeletedDogs.includes(d.name)).map(d => d.name)
               }));
             }
             return; 
          }
          if (isPastUnchangeable && !updatedDaysList[keyToEnsure] && !forceInitialPopulationForPast) {
            updatedDaysList[keyToEnsure] = []; 
            return;
          }
          
          if (!updatedDaysList[keyToEnsure] || !Array.isArray(updatedDaysList[keyToEnsure])) {
            updatedDaysList[keyToEnsure] = [];
          }
          
          let currentDayDogs = [...updatedDaysList[keyToEnsure]];
          const beforeFilterLen = currentDayDogs.length;
          currentDayDogs = currentDayDogs.filter(dog => !permanentlyDeletedDogs.includes(dog.name));
          const afterFilterLen = currentDayDogs.length;
          
          // LOG [PERMA_DEL_DBG]: Filtrado en ensureDayExists (días futuro/ajuste)
          if (afterFilterLen < beforeFilterLen && permanentlyDeletedDogs.length > 0) {
            totalTargetsTouched++;
            const removedDogs = beforeFilterLen - afterFilterLen;
            console.error("[PERMA_DEL_DBG] FILTER_ENSURE_DAY", JSON.stringify({
              profile: selectedUser,
              dayKey: keyToEnsure,
              beforeLen: beforeFilterLen,
              afterLen: afterFilterLen,
              removedCount: removedDogs,
              permanentlyDeletedCount: permanentlyDeletedDogs.length
            }));
          }
          
          const initialDataForDay = getInitialDayData(keyToEnsure, selectedUser);
          const currentDayDogNames = new Set(currentDayDogs.map(d => d.name));

          initialDataForDay.forEach(initialDog => {
              const firstAdded = initialDog.firstAddedDateKey || todayKey;
              const conditionForAddingFixed = initialDog.isFixed && 
                                            (parseDateKey(keyToEnsure) >= parseDateKey(firstAdded) || forceInitialPopulationForPast);

              // NO añadir si está en permanently deleted
              if (permanentlyDeletedDogs.includes(initialDog.name)) {
                console.error("[PERMA_DEL_DBG] SKIP_PERMA_DELETED_INIT", JSON.stringify({
                  profile: selectedUser,
                  dayKey: keyToEnsure,
                  dogName: initialDog.name,
                  reason: "in-permanently-deleted-list"
                }));
                return;
              }

              if (conditionForAddingFixed ) {
                  if (!currentDayDogNames.has(initialDog.name)) {
                      currentDayDogs.push({...initialDog, originalOrder: initialDog.originalOrder !== undefined ? initialDog.originalOrder : Date.now() + Math.random(), isFixed: true, firstAddedDateKey: firstAdded, informativeNote: initialDog.informativeNote || "" });
                      currentDayDogNames.add(initialDog.name);
                  } else {
                     currentDayDogs = currentDayDogs.map(d => d.name === initialDog.name ? {...d, ...initialDog, isFixed: true, firstAddedDateKey: d.firstAddedDateKey || firstAdded, originalOrder: d.originalOrder !== undefined ? d.originalOrder : initialDog.originalOrder, count: d.count || initialDog.count, informativeNote: d.informativeNote || initialDog.informativeNote } : d);
                  }
              }
          });
          currentDayDogs.sort((a,b) => (a.originalOrder || 0) - (b.originalOrder || 0));
          updatedDaysList[keyToEnsure] = currentDayDogs;
        };

        ensureDayExists(newKey, parseDateKey(newKey) < parseDateKey(todayKey) && !updatedDaysList[newKey]);

        // Procesar semanas futuras
        for (let i = 0; i <= MAX_WEEKS_AHEAD_TO_PREPOPULATE; i++) {
          const futureWeekDateKey = addWeeksToDateKey(newKey, i);
          if (!updatedDaysList[futureWeekDateKey]) { 
            ensureDayExists(futureWeekDateKey);
          } else {
            ensureDayExists(futureWeekDateKey); 
          }
        }
        
        // Procesar semanas pasadas
        for (let i = 1; i <= MAX_WEEKS_BEHIND_TO_CONSIDER; i++) {
          const pastWeekDateKey = subWeeksToDateKey(newKey, i);
           if (!updatedDaysList[pastWeekDateKey]) { 
            ensureDayExists(pastWeekDateKey, true); 
          } else if(updatedDaysList[pastWeekDateKey] && Array.isArray(updatedDaysList[pastWeekDateKey])){
             const isPastUnchangeable = parseDateKey(pastWeekDateKey) < parseDateKey(unchangeablePastThresholdDateKey);
             if (!isPastUnchangeable) {
                ensureDayExists(pastWeekDateKey); 
             } else {
                const beforeLen = updatedDaysList[pastWeekDateKey].length;
                updatedDaysList[pastWeekDateKey] = updatedDaysList[pastWeekDateKey].filter(dog => !permanentlyDeletedDogs.includes(dog.name));
                const afterLen = updatedDaysList[pastWeekDateKey].length;
                updatedDaysList[pastWeekDateKey].sort((a,b) => (a.originalOrder || 0) - (b.originalOrder || 0));
                
                // LOG [PERMA_DEL_DBG]: Filtrado en pasado no modificable
                if (afterLen < beforeLen && permanentlyDeletedDogs.length > 0) {
                  totalTargetsTouched++;
                  console.error("[PERMA_DEL_DBG] FILTER_UNCHANGEABLE_PAST_WEEK", JSON.stringify({
                    profile: selectedUser,
                    dayKey: pastWeekDateKey,
                    beforeLen,
                    afterLen,
                    removedCount: beforeLen - afterLen
                  }));
                }
             }
          }
        }
        
        // LOG [PERMA_DEL_DBG]: Resumen final
        if (permanentlyDeletedDogs.length > 0 && totalTargetsTouched > 0) {
          console.error("[PERMA_DEL_DBG] HANDLE_DATE_CHANGE_COMPLETE", JSON.stringify({
            profile: selectedUser,
            newKey,
            permanentlyDeletedCount: permanentlyDeletedDogs.length,
            permanentlyDeletedDogs,
            totalTargetsTouched,
            maxWeeksAhead: MAX_WEEKS_AHEAD_TO_PREPOPULATE,
            maxWeeksBehind: MAX_WEEKS_BEHIND_TO_CONSIDER,
            timestamp: new Date().toISOString()
          }));
        }
        
        return updatedDaysList;
      });

    } else {
      const fallbackDate = new Date();
      setCurrentDate(fallbackDate);
      setSelectedDayKey(formatDateKey(fallbackDate));
    }
  };
